import { z } from 'zod';

import { optionalEnum } from '../../../utils.js';
import { BaseFlowBlockSchema, BlockType } from '../base/baseSchema.js';

export enum RequestBodyFormat {
  None = 'None',
  RawJson = 'RawJson',
  FormUrlEncoded = 'FormUrlEncoded',
}

export enum ResponseBodyFormat {
  AutoDetect = 'AutoDetect',
  ParsedBase64 = 'ParsedBase64',
}

export interface SecureHeader {
  id: string;
  domain?: string;
}

export const METHOD_AND_ENDPOINT_HELPER_TEXT =
  'Enter API method and the full URL for API endpoint.';

export function extractDomain(url?: string): string {
  if (!url) {
    return '';
  }
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

// Domain is optional because we will only ever have a secure header object
// until pressing save at which point we load a list of IDs from the API
// FIXME: Add a proper typeahead for secure headers (instead of BlockAutocomplete)
export const SecureHeaderSchema = z.object({
  id: z.uuid(),
  domain: z.string().optional(),
});

export const RestBlockConfigSchema = z
  .object({
    endpoint: z.string().min(4, {
      message: `Required - ${METHOD_AND_ENDPOINT_HELPER_TEXT}`,
    }),
    secureHeaders: z.array(SecureHeaderSchema).optional(),
    selectedResponseHeaders: z.array(z.string()).optional(),
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
    headers: z
      .array(
        z.object({
          key: z.string().min(1, {
            message: 'Header key cannot be empty.',
          }),
          value: z.string().min(1, {
            message: 'Header value cannot be empty.',
          }),
        })
      )
      .refine(
        (headers) =>
          new Set(headers.map((header) => `${header.key}-${header.value}`)).size === headers.length,
        'Headers cannot have duplicates'
      )
      .optional(),
    bodyFormat: optionalEnum(RequestBodyFormat, RequestBodyFormat.RawJson),
    responseBodyFormat: optionalEnum(ResponseBodyFormat, ResponseBodyFormat.AutoDetect),
    params: z.string().optional(),
    body: z.string().optional(),
  })
  .refine(
    ({ secureHeaders, endpoint }) => {
      if (!secureHeaders) {
        return true;
      }
      const currentDomain = extractDomain(endpoint);
      return secureHeaders.every((header) => {
        return !header.domain || header.domain === currentDomain;
      });
    },
    {
      message: 'One or more secure headers have a domain that does not match the endpoint domain.',
      path: ['secureHeaders'],
    }
  );

export const RestBlockBaseSchema = z.object({
  type: z.literal(BlockType.Rest),
  config: RestBlockConfigSchema,
});

export const RestBlockSchema = BaseFlowBlockSchema.merge(RestBlockBaseSchema);

export type RestBlock = z.infer<typeof RestBlockSchema>;
